import React, { ChangeEvent, useMemo, useState } from 'react';
import { Select, Form, Typography, Col, Alert, Space, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { POPULAR_MAKES } from '../../../mysearch/mysearchConstants';
import { DEFAULT_CAR_IMAGE } from '../../../components/SelectWithImages';
import { ExclamationCircleOutlined, PlusOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { FieldConfig } from '../../types/fieldTypes';
import { useValidator } from '../../hooks/useValidator';
import { useApiOptions } from '../../hooks/useApiOptions';
import { MissingVehicleModal } from '../MissingVehicleModal';
import { VehicleRequestType } from '../../../api/services/vehicleRequestService';

const { Text } = Typography;

interface SelectFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

/** Keys consumed locally — must NOT be forwarded to <Select> */
const EXCLUDED_CUSTOM_PROPS = [
  'onFieldChange',
  'onFormChange',
  'description',
  'disabled',
  'placeholder',
  'allowRequest',
  'requestType',
  'requestContextMakeField',
  'requestContextMakeNameField',
  'requestContextModelField',
  'requestContextModelNameField',
];

export const SelectField: React.FC<SelectFieldProps> = ({
  field,
  value,
  onChange,
  mode,
  formData = {},
}) => {
  const { t } = useTranslation();
  const { touched, validationError, handleBlur, handleValidate } = useValidator(
    { field, value, formData }
  );

  const {
    options: apiOptions,
    loading: apiLoading,
    error: apiError,
  } = useApiOptions({ apiConfig: field.api, formData });

  const allOptions = useMemo(() => {
    if (field.api) return apiOptions;
    return field.options || [];
  }, [field.api, field.options, apiOptions, field.name]);

  const isMakeField = field.customProps?.requestType === 'make' || field.name === 'make';
  const popularOptions = useMemo(() => {
    if (!isMakeField) return [];
    return allOptions.filter((o: { value: string; label: string }) =>
      POPULAR_MAKES.some(pm => pm.toLowerCase() === o.label.toLowerCase())
    );
  }, [isMakeField, allOptions]);

  // ── "Can't find it?" feature ──────────────────────────────────────
  const allowRequest = !!field.customProps?.allowRequest;
  const requestType = field.customProps?.requestType as VehicleRequestType | undefined;

  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const requestContext = useMemo(() => {
    if (!allowRequest) return {};
    const makeField = field.customProps?.requestContextMakeField as string | undefined;
    const makeNameField = field.customProps?.requestContextMakeNameField as string | undefined;
    const modelField = field.customProps?.requestContextModelField as string | undefined;
    const modelNameField = field.customProps?.requestContextModelNameField as string | undefined;
    return {
      makeId: makeField ? formData[makeField] : undefined,
      makeName: makeNameField ? formData[makeNameField] : undefined,
      modelId: modelField ? formData[modelField] : undefined,
      modelName: modelNameField ? formData[modelNameField] : undefined,
    };
  }, [allowRequest, field.customProps, formData]);

  // ── Change handler ────────────────────────────────────────────────
  const handleChange = (selectedValue: string) => {
    const syntheticEvent = {
      target: { name: field.name, value: selectedValue },
    } as ChangeEvent<HTMLSelectElement>;

    onChange(syntheticEvent);
    handleValidate(selectedValue);

    if (field.customProps?.onFieldChange) {
      field.customProps.onFieldChange(selectedValue, field.name, formData);
    }
    if (field.customProps?.onFormChange) {
      const updatedFormData = { ...formData, [field.name]: selectedValue };
      field.customProps.onFormChange(updatedFormData, field.name);
    }
  };

  // ── View mode ─────────────────────────────────────────────────────
  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Form.Item label={<Text type="secondary">{field.label}</Text>}>
          <Select value={value || undefined} disabled style={{ width: '100%' }} placeholder={t('select_field.na')} />
        </Form.Item>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  const forwardedCustomProps = field.customProps
    ? Object.fromEntries(
        Object.entries(field.customProps).filter(([key]) => !EXCLUDED_CUSTOM_PROPS.includes(key))
      )
    : {};

  // ── Dropdown footer ───────────────────────────────────────────────
  const dropdownRender =
    allowRequest && requestType
      ? (menu: React.ReactNode) => (
          <>
            {menu}
            <Divider style={{ margin: '4px 0 0' }} />
            {/* Request row — styled as a full-width action item */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setModalOpen(true)}
              onKeyDown={e => e.key === 'Enter' && setModalOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                cursor: 'pointer',
                background: '#fffaf8',
                transition: 'background 0.15s ease',
                userSelect: 'none',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.background = '#fff3ed';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.background = '#fffaf8';
              }}
            >
              {/* Orange icon pill */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: '#FE5A1D',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(254,90,29,0.3)',
                }}
              >
                <PlusOutlined style={{ color: '#fff', fontSize: 12 }} />
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151', lineHeight: 1.3 }}>
                  {t('select_field.cant_find', { type: requestType })}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.3, marginTop: 1 }}>
                  {t('select_field.request_added')}
                </div>
              </div>

              {/* Arrow */}
              <ArrowRightOutlined style={{ fontSize: 11, color: '#FE5A1D', flexShrink: 0 }} />
            </div>
          </>
        )
      : undefined;

  return (
    <>
      <Col {...colProps} className={field.layout?.className}>
        <Form.Item
          label={
            <Space>
              {field.label}
              {apiLoading && <Text type="secondary">(Loading...)</Text>}
            </Space>
          }
          required={field.required}
          validateStatus={validationError && touched ? 'error' : ''}
          help={
            <Space direction="vertical" size="small">
              {field.customProps?.description && (
                <Text type="secondary">{field.customProps.description}</Text>
              )}
              {apiError && (
                <Alert
                  message={apiError}
                  type="error"
                  icon={<ExclamationCircleOutlined />}
                  showIcon
                />
              )}
              {validationError && touched && (
                <Text type="danger">{validationError}</Text>
              )}
            </Space>
          }
        >
          <Select
            placeholder={
              apiLoading
                ? t('select_field.loading_options')
                : field.customProps?.placeholder || t('select_field.select_option')
            }
            value={value || undefined}
            onChange={handleChange}
            onBlur={handleBlur}
            loading={apiLoading}
            disabled={field.customProps?.disabled || apiLoading}
            size="large"
            style={{ width: '100%' }}
            status={validationError && touched ? 'error' : ''}
            showSearch={allowRequest}
            onSearch={allowRequest ? setSearchText : undefined}
            filterOption={
              allowRequest
                ? (input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                : undefined
            }
            dropdownRender={dropdownRender}
            {...forwardedCustomProps}
          >
            {isMakeField && popularOptions.length > 0 ? (
              <>
                <Select.OptGroup label="Popular Makes">
                  {popularOptions.map((option: { value: string; label: string; image?: string }) => (
                    <Select.Option key={`pop-${option.value}`} value={option.value} label={option.label}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                          src={option.image || DEFAULT_CAR_IMAGE}
                          alt={option.label}
                          style={{ width: '20px', height: '20px', objectFit: 'contain', opacity: option.image ? 1 : 0.45 }}
                          onError={e => { e.currentTarget.src = DEFAULT_CAR_IMAGE; e.currentTarget.style.opacity = '0.45'; }}
                        />
                        <span>{option.label}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
                <Select.OptGroup label="All Makes">
                  {allOptions.map((option: { value: string; label: string; image?: string }) => (
                    <Select.Option key={option.value} value={option.value} label={option.label}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                          src={option.image || DEFAULT_CAR_IMAGE}
                          alt={option.label}
                          style={{ width: '20px', height: '20px', objectFit: 'contain', opacity: option.image ? 1 : 0.45 }}
                          onError={e => { e.currentTarget.src = DEFAULT_CAR_IMAGE; e.currentTarget.style.opacity = '0.45'; }}
                        />
                        <span>{option.label}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              </>
            ) : (
              allOptions.map((option: { value: string; label: string; image?: string }) => (
                <Select.Option key={option.value} value={option.value} label={option.label}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                      src={option.image || DEFAULT_CAR_IMAGE}
                      alt={option.label}
                      style={{ width: '20px', height: '20px', objectFit: 'contain', opacity: option.image ? 1 : 0.45 }}
                      onError={e => { e.currentTarget.src = DEFAULT_CAR_IMAGE; e.currentTarget.style.opacity = '0.45'; }}
                    />
                    <span>{option.label}</span>
                  </div>
                </Select.Option>
              ))
            )}
          </Select>
        </Form.Item>
      </Col>

      {allowRequest && requestType && (
        <MissingVehicleModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          requestType={requestType}
          initialName={searchText}
          context={requestContext}
        />
      )}
    </>
  );
};
